import { Alert, Badge, Button, Group, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { PhoneInput } from "./PhoneInput";
import { useAuth } from "../context/useAuth";
import { usersApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import { PHONE_OTP_DELIVERY_NOTE, phoneOtpSentMessage } from "../lib/otpCopy";
import { parsePhoneLocal, toPhoneForApi } from "../lib/phone";

export function PhoneVerificationCard() {
  const { user, setUser } = useAuth();
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [otpSentTo, setOtpSentTo] = useState<string | null>(null);

  if (!user) return null;

  const verified = !!user.phone_verified_at;
  const hasPhone = !!user.phone;
  const isHr = user.role === "company_user";
  const isAffiliate = user.role === "affiliate";
  const blurb = isHr
    ? `Verify your mobile number to get updates on submissions and hiring activity. ${PHONE_OTP_DELIVERY_NOTE}`
    : isAffiliate
      ? `Verify your mobile number before creating and sharing your referral link. ${PHONE_OTP_DELIVERY_NOTE}`
      : `Verify your mobile number to submit candidates and receive updates. ${PHONE_OTP_DELIVERY_NOTE}`;

  async function handleSavePhone() {
    setSavingPhone(true);
    try {
      const updated = await usersApi.updateMe({ phone: toPhoneForApi(phoneInput) });
      setUser(updated);
      setPhoneInput("");
      notifications.show({ color: "green", title: "Phone saved", message: "You can now request an OTP." });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not save phone";
      notifications.show({ color: "red", title: "Failed", message });
    } finally {
      setSavingPhone(false);
    }
  }

  async function handleRequestOtp() {
    setSending(true);
    try {
      const result = await usersApi.requestPhoneOtp();
      setOtpSent(true);
      setOtpSentTo(result.phone ?? null);
      notifications.show({
        color: "green",
        title: "Code sent",
        message: phoneOtpSentMessage(result.phone),
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not send OTP";
      notifications.show({ color: "red", title: "Failed", message });
    } finally {
      setSending(false);
    }
  }

  async function handleVerify() {
    setVerifying(true);
    try {
      const updated = await usersApi.verifyPhoneOtp(code);
      setUser(updated);
      setCode("");
      setOtpSent(false);
      notifications.show({
        color: "green",
        title: "Verified",
        message: "Your phone is verified.",
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Could not verify OTP";
      notifications.show({ color: "red", title: "Failed", message });
    } finally {
      setVerifying(false);
    }
  }

  return (
    <Paper withBorder p="lg">
      <Group justify="space-between" mb="sm">
        <Title order={4}>Phone verification</Title>
        {verified ? (
          <Badge color="green" variant="light">
            Verified
          </Badge>
        ) : (
          <Badge color="orange" variant="light">
            Not verified
          </Badge>
        )}
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        {blurb}
      </Text>
      {verified ? (
        <Text fw="var(--axio-font-weight-medium)">{user.phone}</Text>
      ) : !hasPhone ? (
        <Stack>
          <Alert color="yellow">Add your phone number to receive an OTP.</Alert>
          <PhoneInput
            label="Phone"
            value={phoneInput}
            onChange={setPhoneInput}
          />
          <Button
            color="violet"
            loading={savingPhone}
            onClick={handleSavePhone}
            disabled={parsePhoneLocal(phoneInput).length < 10}
          >
            Save phone
          </Button>
        </Stack>
      ) : (
        <Stack>
          <Text fw="var(--axio-font-weight-medium)">{user.phone}</Text>
          <Button color="violet" loading={sending} onClick={handleRequestOtp}>
            Send OTP to phone
          </Button>
          {otpSent && (
            <>
              {otpSentTo && (
                <Alert variant="light" color="blue">
                  {phoneOtpSentMessage(otpSentTo)}
                </Alert>
              )}
              <TextInput
                label="OTP code"
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
              />
              <Button color="green" loading={verifying} onClick={handleVerify} disabled={code.trim().length < 4}>
                Verify phone
              </Button>
            </>
          )}
        </Stack>
      )}
    </Paper>
  );
}
